import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { startOfMonth, endOfMonth, subMonths, format } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

// Filter dimension types
export type FilterDimension = 
  | 'date'
  | 'country'
  | 'region'
  | 'businessUnit'
  | 'department'
  | 'function'
  | 'level'
  | 'grade'
  | 'employmentType'
  | 'manager'
  | 'team'
  | 'gender'
  | 'tenureBand'
  | 'performanceBand'
  | 'attritionRiskBand';

// Filter value types
export interface FilterValue {
  id: string;
  label: string;
  count?: number;
  disabled?: boolean;
  parentId?: string; // For hierarchical filters
}

// Date range type
export interface DateRange {
  start: Date;
  end: Date;
  preset?: 'today' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter' | 'thisYear' | 'lastYear' | 'ytd' | 'custom';
}

// Individual filter state
export interface FilterState {
  dimension: FilterDimension;
  values: string[]; // Selected value IDs
  availableValues: FilterValue[];
  isLoading: boolean;
  operator: 'in' | 'notIn' | 'between' | 'equals';
}

// Saved view
export interface SavedView {
  id: string;
  name: string;
  description?: string;
  filters: Record<FilterDimension, FilterState>;
  createdAt: Date;
  updatedAt: Date;
  isDefault?: boolean;
  isShared?: boolean;
  owner: string;
}

// Filter performance metrics
export interface FilterPerformanceMetrics {
  lastAppliedAt?: Date;
  renderTime?: number;
  queryTime?: number;
  cacheHit?: boolean;
}

// Main filters state
export interface EnhancedFiltersState {
  // Active filters
  activeFilters: Partial<Record<FilterDimension, FilterState>>;
  
  // Date range (special handling)
  dateRange: DateRange;
  
  // Quick filters (pinned to top)
  quickFilters: FilterDimension[];
  
  // Filter dependencies
  dependencies: Partial<Record<FilterDimension, FilterDimension[]>>;
  
  // Saved views
  savedViews: SavedView[];
  currentViewId?: string;
  
  // UI state
  drawerOpen: boolean;
  expandedSections: FilterDimension[];
  searchQuery: string;
  
  // Performance
  performance: FilterPerformanceMetrics;
  
  // Data quality
  totalRecordCount: number;
  filteredRecordCount: number;
  privacyThreshold: number;
  
  // Cross-filtering
  crossFilterSource?: {
    widgetId: string;
    dimension: FilterDimension;
    value: string;
  };
  
  // Settings
  timezone: string;
  locale: string;
  
  // State tracking
  isDirty: boolean;
  isLoading: boolean;
  error?: string;
}

// Default Warsaw timezone
const WARSAW_TZ = 'Europe/Warsaw';

// Get last full month in Warsaw timezone
const getLastFullMonth = (): DateRange => {
  const now = utcToZonedTime(new Date(), WARSAW_TZ);
  const lastMonth = subMonths(now, 1);
  return {
    start: startOfMonth(lastMonth),
    end: endOfMonth(lastMonth),
    preset: 'lastMonth'
  };
};

// Initial state
const initialState: EnhancedFiltersState = {
  activeFilters: {},
  dateRange: getLastFullMonth(),
  quickFilters: ['date', 'country', 'businessUnit'],
  dependencies: {
    country: ['region', 'manager', 'team'],
    businessUnit: ['department', 'function', 'team'],
    department: ['team', 'manager'],
    manager: ['team'],
  },
  savedViews: [],
  drawerOpen: false,
  expandedSections: [],
  searchQuery: '',
  performance: {},
  totalRecordCount: 0,
  filteredRecordCount: 0,
  privacyThreshold: 5,
  timezone: WARSAW_TZ,
  locale: 'en-US',
  isDirty: false,
  isLoading: false,
};

const enhancedFiltersSlice = createSlice({
  name: 'enhancedFilters',
  initialState,
  reducers: {
    // Set filter values
    setFilter: (state, action: PayloadAction<{
      dimension: FilterDimension;
      values: string[];
      operator?: FilterState['operator'];
    }>) => {
      const { dimension, values, operator = 'in' } = action.payload;
      
      if (!state.activeFilters[dimension]) {
        state.activeFilters[dimension] = {
          dimension,
          values: [],
          availableValues: [],
          isLoading: false,
          operator,
        };
      }
      
      state.activeFilters[dimension]!.values = values;
      state.activeFilters[dimension]!.operator = operator;
      state.isDirty = true;
      
      // Clear dependent filters if parent changes
      const dependents = state.dependencies[dimension];
      if (dependents) {
        dependents.forEach(dep => {
          if (state.activeFilters[dep]) {
            state.activeFilters[dep]!.values = [];
          }
        });
      }
    },
    
    // Add filter value (multi-select)
    addFilterValue: (state, action: PayloadAction<{
      dimension: FilterDimension;
      value: string;
    }>) => {
      const { dimension, value } = action.payload;
      
      if (!state.activeFilters[dimension]) {
        state.activeFilters[dimension] = {
          dimension,
          values: [value],
          availableValues: [],
          isLoading: false,
          operator: 'in',
        };
      } else if (!state.activeFilters[dimension]!.values.includes(value)) {
        state.activeFilters[dimension]!.values.push(value);
      }
      
      state.isDirty = true;
    },
    
    // Remove filter value
    removeFilterValue: (state, action: PayloadAction<{
      dimension: FilterDimension;
      value: string;
    }>) => {
      const { dimension, value } = action.payload;
      
      if (state.activeFilters[dimension]) {
        state.activeFilters[dimension]!.values = 
          state.activeFilters[dimension]!.values.filter(v => v !== value);
          
        if (state.activeFilters[dimension]!.values.length === 0) {
          delete state.activeFilters[dimension];
        }
      }
      
      state.isDirty = true;
    },
    
    // Clear specific filter
    clearFilter: (state, action: PayloadAction<FilterDimension>) => {
      delete state.activeFilters[action.payload];
      state.isDirty = true;
      
      // Clear dependent filters
      const dependents = state.dependencies[action.payload];
      if (dependents) {
        dependents.forEach(dep => {
          delete state.activeFilters[dep];
        });
      }
    },
    
    // Clear all filters
    clearAllFilters: (state) => {
      state.activeFilters = {};
      state.dateRange = getLastFullMonth();
      state.crossFilterSource = undefined;
      state.isDirty = false;
    },
    
    // Set date range
    setDateRange: (state, action: PayloadAction<DateRange>) => {
      state.dateRange = action.payload;
      state.isDirty = true;
    },
    
    // Set available values for a filter
    setAvailableValues: (state, action: PayloadAction<{
      dimension: FilterDimension;
      values: FilterValue[];
    }>) => {
      const { dimension, values } = action.payload;
      
      if (!state.activeFilters[dimension]) {
        state.activeFilters[dimension] = {
          dimension,
          values: [],
          availableValues: values,
          isLoading: false,
          operator: 'in',
        };
      } else {
        state.activeFilters[dimension]!.availableValues = values;
      }
    },
    
    // Cross-filter from widget
    setCrossFilter: (state, action: PayloadAction<{
      widgetId: string;
      dimension: FilterDimension;
      value: string;
    }>) => {
      const { widgetId, dimension, value } = action.payload;
      
      state.crossFilterSource = { widgetId, dimension, value };
      
      // Apply as regular filter
      if (!state.activeFilters[dimension]) {
        state.activeFilters[dimension] = {
          dimension,
          values: [value],
          availableValues: [],
          isLoading: false,
          operator: 'in',
        };
      } else {
        state.activeFilters[dimension]!.values = [value];
      }
      
      state.isDirty = true;
    },
    
    // Clear cross-filter
    clearCrossFilter: (state) => {
      if (state.crossFilterSource) {
        const dimension = state.crossFilterSource.dimension;
        delete state.activeFilters[dimension];
        state.crossFilterSource = undefined;
        state.isDirty = true;
      }
    },
    
    // Save current filters as view
    saveView: (state, action: PayloadAction<{
      name: string;
      description?: string;
      isShared?: boolean;
    }>) => {
      const { name, description, isShared } = action.payload;
      
      const newView: SavedView = {
        id: `view_${Date.now()}`,
        name,
        description,
        filters: { ...state.activeFilters } as Record<FilterDimension, FilterState>,
        createdAt: new Date(),
        updatedAt: new Date(),
        isShared,
        owner: 'current_user', // Would come from auth context
      };
      
      state.savedViews.push(newView);
      state.currentViewId = newView.id;
      state.isDirty = false;
    },
    
    // Load saved view
    loadView: (state, action: PayloadAction<string>) => {
      const view = state.savedViews.find(v => v.id === action.payload);
      if (view) {
        state.activeFilters = { ...view.filters };
        state.currentViewId = view.id;
        state.isDirty = false;
      }
    },
    
    // Delete saved view
    deleteView: (state, action: PayloadAction<string>) => {
      state.savedViews = state.savedViews.filter(v => v.id !== action.payload);
      if (state.currentViewId === action.payload) {
        state.currentViewId = undefined;
      }
    },
    
    // Toggle drawer
    toggleDrawer: (state) => {
      state.drawerOpen = !state.drawerOpen;
    },
    
    // Toggle section expansion
    toggleSection: (state, action: PayloadAction<FilterDimension>) => {
      const dimension = action.payload;
      const index = state.expandedSections.indexOf(dimension);
      
      if (index >= 0) {
        state.expandedSections.splice(index, 1);
      } else {
        state.expandedSections.push(dimension);
      }
    },
    
    // Set search query
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    
    // Update performance metrics
    updatePerformance: (state, action: PayloadAction<FilterPerformanceMetrics>) => {
      state.performance = { ...state.performance, ...action.payload };
    },
    
    // Update record counts
    updateRecordCounts: (state, action: PayloadAction<{
      total: number;
      filtered: number;
    }>) => {
      state.totalRecordCount = action.payload.total;
      state.filteredRecordCount = action.payload.filtered;
    },
    
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    // Set error
    setError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    
    // Set locale
    setLocale: (state, action: PayloadAction<string>) => {
      state.locale = action.payload;
    },
    
    // Restore from localStorage
    restoreFromStorage: (state, action: PayloadAction<Partial<EnhancedFiltersState>>) => {
      Object.assign(state, action.payload);
    },
  },
});

// Export actions
export const {
  setFilter,
  addFilterValue,
  removeFilterValue,
  clearFilter,
  clearAllFilters,
  setDateRange,
  setAvailableValues,
  setCrossFilter,
  clearCrossFilter,
  saveView,
  loadView,
  deleteView,
  toggleDrawer,
  toggleSection,
  setSearchQuery,
  updatePerformance,
  updateRecordCounts,
  setLoading,
  setError,
  setLocale,
  restoreFromStorage,
} = enhancedFiltersSlice.actions;

// Selectors
export const selectActiveFilters = (state: { enhancedFilters: EnhancedFiltersState }) => 
  state.enhancedFilters.activeFilters;

export const selectDateRange = (state: { enhancedFilters: EnhancedFiltersState }) => 
  state.enhancedFilters.dateRange;

export const selectFilteredCount = (state: { enhancedFilters: EnhancedFiltersState }) => 
  state.enhancedFilters.filteredRecordCount;

export const selectIsPrivacyProtected = (state: { enhancedFilters: EnhancedFiltersState }) => 
  state.enhancedFilters.filteredRecordCount < state.enhancedFilters.privacyThreshold;

export const selectSavedViews = (state: { enhancedFilters: EnhancedFiltersState }) => 
  state.enhancedFilters.savedViews;

export const selectIsDrawerOpen = (state: { enhancedFilters: EnhancedFiltersState }) => 
  state.enhancedFilters.drawerOpen;

// Export reducer
export default enhancedFiltersSlice.reducer;